import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useParams } from "wouter";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Save,
  Search,
  User,
  Plus,
  Printer,
  RotateCcw,
  Send,
  Loader2,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Edit2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { useErrorDialog } from "@/components/ui/error-dialog";
import { CategoryTypeDialog } from "./dynamic-form/CategoryTypeDialog";
import { DynamicForm, FormSection } from "./dynamic-form/DynamicForm";
import { cn } from "@/lib/utils";

export default function DynamicRequestForm() {
  const [, navigate] = useLocation();
  const params = useParams<{ id?: string }>();
  const editId = params.id ? parseInt(params.id) : null;
  const isEditMode = !!editId;
  
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // State
  const [showCategoryDialog, setShowCategoryDialog] = useState(!isEditMode);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [activeSection, setActiveSection] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Error dialog
  const { showError, ErrorDialogComponent } = useErrorDialog();

  // Fetch existing request for edit mode
  const { data: existingRequest, isLoading: loadingExisting } = trpc.requests.getById.useQuery(
    { id: editId! },
    { enabled: isEditMode }
  );

  // Fetch categories with types
  const { data: categories, isLoading: loadingCategories } =
    trpc.requestConfig.categories.list.useQuery();

  // Fetch form definition for selected types
  const { data: formDefinition, isLoading: loadingForm } =
    trpc.requestConfig.formDefinition.getForTypes.useQuery(
      { typeIds: selectedTypeIds },
      { enabled: selectedTypeIds.length > 0 }
    );

  // Fetch material types for mapping IDs to names
  const { data: materialTypesData } = trpc.requestConfig.fields.getDataSourceOptions.useQuery(
    { source: 'material_types' }
  );

  // Create a map of material type ID to name
  const materialTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    if (materialTypesData && Array.isArray(materialTypesData)) {
      materialTypesData.forEach((item: any) => {
        map.set(String(item.value), item.label);
      });
    }
    return map;
  }, [materialTypesData]);

  // Get selected category
  const selectedCategory = useMemo(() => {
    if (!selectedCategoryId || !categories) return null;
    return (categories as any[]).find((c: any) => c.id === selectedCategoryId);
  }, [selectedCategoryId, categories]);

  // Get selected types
  const selectedTypes = useMemo(() => {
    if (!selectedCategory?.types || selectedTypeIds.length === 0) return [];
    return selectedCategory.types.filter((t: any) => selectedTypeIds.includes(t.id));
  }, [selectedCategory, selectedTypeIds]);

  // Helper function to convert material type ID to name
  const getMaterialTypeName = (typeId: string | number): string => {
    return materialTypeMap.get(String(typeId)) || String(typeId);
  };

  // Create request mutation
  const createRequest = trpc.requests.create.useMutation({
    onSuccess: (data: any) => {
      toast.success(
        t("requests.created", "Request {{number}} created successfully!", {
          number: data.requestNumber,
        })
      );
      navigate("/requests");
    },
    onError: (error: any) => {
      const isNetworkError =
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        error?.message?.includes("network") ||
        error?.message?.includes("aborted") ||
        error?.cause?.name === "AbortError" ||
        !navigator.onLine;
      if (isNetworkError) {
        toast.error(
          t("requests.networkError", "No internet connection. Please check your network and try again."),
          { duration: 8000, id: "network-error" }
        );
      } else {
        showError(
          t(
            "requests.createError",
            "There was an error processing your request. Please try again or contact support if the problem persists."
          ),
          t("requests.submissionFailed", "Request Submission Failed")
        );
      }
      console.error("Request creation error:", error);
    },
  });

  // Update request mutation
  const updateRequest = trpc.requests.update.useMutation({
    onSuccess: (data: any) => {
      toast.success(
        t("requests.updated", "Request {{number}} updated successfully!", {
          number: data.requestNumber,
        })
      );
      navigate("/requests");
    },
    onError: (error: any) => {
      const isNetworkError =
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        error?.message?.includes("network") ||
        error?.message?.includes("aborted") ||
        error?.cause?.name === "AbortError" ||
        !navigator.onLine;
      if (isNetworkError) {
        toast.error(
          t("requests.networkError", "No internet connection. Please check your network and try again."),
          { duration: 8000, id: "network-error" }
        );
      } else {
        showError(
          t(
            "requests.updateError",
            "There was an error updating your request. Please try again or contact support if the problem persists."
          ),
          t("requests.updateFailed", "Request Update Failed")
        );
      }
      console.error("Request update error:", error);
    },
  });

  // Load existing request data in edit mode
  useEffect(() => {
    if (isEditMode && existingRequest && !isDataLoaded) {
      // Check if request is editable (draft status only)
      if (existingRequest.status !== "draft") {
        toast.error(t("requests.notEditable", "Only draft requests can be edited"));
        navigate("/requests");
        return;
      }

      // Set category and types from existing request
      if (existingRequest.categoryId) {
        setSelectedCategoryId(existingRequest.categoryId);
      }
      if (existingRequest.selectedTypeIds && Array.isArray(existingRequest.selectedTypeIds)) {
        setSelectedTypeIds(existingRequest.selectedTypeIds);
      }
      
      // Set form data from existing request
      if (existingRequest.formData && typeof existingRequest.formData === 'object') {
        const existingData = existingRequest.formData as Record<string, any>;
        setFormData({ ...existingData });
      }
      
      setShowCategoryDialog(false);
      setIsDataLoaded(true);
    }
  }, [isEditMode, existingRequest, isDataLoaded, navigate, t]);

  // Initialize form data with defaults when form definition loads (only for new requests)
  useEffect(() => {
    if (formDefinition?.sections && !isEditMode) {
      const defaults: Record<string, any> = {};
      formDefinition.sections.forEach((section: any) => {
        if (section.isRepeatable) {
          defaults[section.code] = [];
        } else {
          section.fields.forEach((field: any) => {
            if (field.defaultValue) {
              defaults[field.code] = field.defaultValue;
            }
          });
        }
      });
      setFormData((prev) => ({ ...defaults, ...prev }));

      if (formDefinition.sections.length > 0 && !activeSection) {
        setActiveSection(formDefinition.sections[0].code);
      }
    }
  }, [formDefinition, isEditMode]);

  // Set active section when form definition loads in edit mode
  useEffect(() => {
    if (formDefinition?.sections && isEditMode && formDefinition.sections.length > 0 && !activeSection) {
      setActiveSection(formDefinition.sections[0].code);
    }
  }, [formDefinition, isEditMode, activeSection]);

  // Prefill user info (only for new requests)
  useEffect(() => {
    if (user && !isEditMode) {
      setFormData((prev) => ({
        ...prev,
        requestor_name: user.name || "",
        email: user.email || "",
        company: (user as any).companyName || "Centre3",
        department: (user as any).departmentName || "",
        mobile: (user as any).phone || "",
      }));
    }
  }, [user, isEditMode, formDefinition]);

  // Location auto-fill: when country changes, fetch all single-option location values in one server call
  const countryValue = formData.country ? String(formData.country) : undefined;
  const { data: locationAutoFill } = trpc.requestConfig.fields.getLocationAutoFill.useQuery(
    { countryValue: countryValue! },
    { enabled: !!countryValue }
  );

  // Apply location auto-fill when server returns single-option values
  const prevCountryRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!locationAutoFill || !countryValue) return;
    // Only apply when country actually changed (not on initial load with existing data)
    if (prevCountryRef.current === countryValue) return;
    prevCountryRef.current = countryValue;
    // Only auto-fill fields that are currently empty
    setFormData((prev) => {
      const updated = { ...prev };
      if (locationAutoFill.region && (!prev.region || prev.region === '')) {
        updated.region = locationAutoFill.region.value;
      }
      if (locationAutoFill.city && (!prev.city || prev.city === '')) {
        updated.city = locationAutoFill.city.value;
      }
      if (locationAutoFill.site && (!prev.site || prev.site === '')) {
        updated.site = locationAutoFill.site.value;
      }
      if (locationAutoFill.zone && (!prev.zone || prev.zone === '')) {
        updated.zone = locationAutoFill.zone.value;
      }
      if (locationAutoFill.area && (!prev.area || prev.area === '')) {
        updated.area = locationAutoFill.area.value;
      }
      return updated;
    });
  }, [locationAutoFill, countryValue]);

  // Handle category/type selection from dialog
  const handleCategoryTypeConfirm = (categoryId: number, typeIds: number[]) => {
    setSelectedCategoryId(categoryId);
    setSelectedTypeIds(typeIds);
    if (!isEditMode) {
      setFormData({});
    }
    setActiveSection("");
    setShowCategoryDialog(false);
  };

  // Handle change selection
  const handleChangeSelection = () => {
    setShowCategoryDialog(true);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formDefinition?.sections) return false;

    formDefinition.sections.forEach((section: any) => {
      // Check if section should be visible based on conditions
      const isSectionVisible = !section.showCondition || (() => {
        const { field, operator = "equals", value } = section.showCondition;
        const fieldValue = formData[field];
        
        switch (operator) {
          case "equals":
            return fieldValue === value;
          case "not_equals":
            return fieldValue !== value;
          case "in":
            return Array.isArray(value) && value.includes(fieldValue);
          case "not_empty":
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
          case "empty":
            return fieldValue === undefined || fieldValue === null || fieldValue === "";
          default:
            return true;
        }
      })();

      // Skip validation for hidden sections
      if (!isSectionVisible) return;

      if (section.isRepeatable) {
        const items = formData[section.code] || [];
        
        // Enforce mandatory visitor for sections with visitor fields
        if (section.code === "visitors" && items.length === 0) {
          newErrors[section.code] = t(
            "validation.visitorRequired",
            "At least one visitor is required"
          );
        }
        
        // Skip minItems validation for optional material sections
        // material_entry and material_exit are optional - not always both required
        const isOptionalMaterialSection = section.code === "material_entry" || section.code === "material_exit";
        
        if (section.minItems && items.length < section.minItems && !isOptionalMaterialSection) {
          newErrors[section.code] = t(
            "validation.minItems",
            "At least {{min}} items required",
            { min: section.minItems }
          );
        }
        items.forEach((item: any, index: number) => {
          section.fields.forEach((field: any) => {
            // Check field visibility within repeatable sections
            const isFieldVisible = !field.showCondition || (() => {
              const { field: conditionField, operator = "equals", value } = field.showCondition;
              const fieldValue = item[conditionField] || formData[conditionField];
              
              switch (operator) {
                case "equals":
                  return fieldValue === value;
                case "not_equals":
                  return fieldValue !== value;
                case "in":
                  return Array.isArray(value) && value.includes(fieldValue);
                case "not_empty":
                  return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
                case "empty":
                  return fieldValue === undefined || fieldValue === null || fieldValue === "";
                default:
                  return true;
              }
            })();

            if (isFieldVisible && field.isRequired && !item[field.code]) {
              newErrors[`${section.code}[${index}].${field.code}`] = t(
                "validation.required",
                "This field is required"
              );
            }
          });
        });
      } else {
        section.fields.forEach((field: any) => {
          // Skip validation for readonly fields with user_profile source - they are auto-populated from user profile
          if (field.fieldType === "readonly" && field.optionsSource === "user_profile") {
            return;
          }

          // Check field visibility
          const isFieldVisible = !field.showCondition || (() => {
            const { field: conditionField, operator = "equals", value } = field.showCondition;
            const fieldValue = formData[conditionField];
            
            switch (operator) {
              case "equals":
                return fieldValue === value;
              case "not_equals":
                return fieldValue !== value;
              case "in":
                return Array.isArray(value) && value.includes(fieldValue);
              case "not_empty":
                return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
              case "empty":
                return fieldValue === undefined || fieldValue === null || fieldValue === "";
              default:
                return true;
            }
          })();

          if (isFieldVisible && field.isRequired && !formData[field.code]) {
            // Namespace by section code to prevent collision when multiple sections share the same field code (e.g. 'description')
            newErrors[`${section.code}.${field.code}`] = t("validation.required", "This field is required");
          }
        });
      }
      
      // Validate visitor info is mandatory only if the form section has a visitors field
      if (section.code === "visitors" || section.fields.some((f: any) => f.code === "visitors")) {
        const visitors = formData.visitors || [];
        if (visitors.length === 0) {
          newErrors.visitors = t("validation.visitorRequired", "At least one visitor is required");
        } else {
          visitors.forEach((visitor: any, index: number) => {
            if (!visitor.full_name || !visitor.id_number) {
              newErrors[`visitors[${index}]`] = t("validation.visitorInfoRequired", "Visitor name and ID number are required");
            }
          });
        }
      }
    });

    // Cross-field date/time validation
    if (formData.start_date && formData.end_date) {
      if (formData.end_date < formData.start_date) {
        newErrors.end_date = t("validation.endDateBeforeStart", "End date must be on or after start date");
      }
      // If same date, validate times
      if (formData.start_date === formData.end_date && formData.start_time && formData.end_time) {
        if (formData.end_time <= formData.start_time) {
          newErrors.end_time = t("validation.endTimeBeforeStart", "End time must be after start time");
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (asDraft: boolean = false) => {
    if (!asDraft && !validateForm()) {
      toast.error(t("requests.validationError", "Please fill in all required fields"));
      return;
    }

    // Ensure user profile readonly fields are included in formData for submission
    const submissionData = { ...formData };
    if (user) {
      if (!submissionData.requestor_name) submissionData.requestor_name = user.name || user.email || "";
      if (!submissionData.email) submissionData.email = user.email || "";
      if (!submissionData.company) submissionData.company = (user as any).companyName || "Centre3";
      if (!submissionData.department) submissionData.department = (user as any).departmentName || "";
      if (!submissionData.mobile) submissionData.mobile = (user as any).phone || "";
    }

    const visitors = submissionData.visitors || [];
    const mainVisitor = visitors[0] || {
      full_name: user?.name || "Unknown",
      id_type: "national_id",
      id_number: "N/A",
    };
    const hostUser = submissionData.host_user;
    const resolvedHostId =
      typeof hostUser === "object" && hostUser !== null
        ? parseInt(hostUser.id)
        : parseInt(hostUser);

    // Always send notifications via both email and SMS
    const notificationMode = 'both' as const;

    const requestData = {
      categoryId: selectedCategoryId!,
      selectedTypeIds,
      formData: submissionData,
      notificationMode: 'both', // Always use both Email and SMS
      visitorName: mainVisitor.full_name || mainVisitor.name,
      visitorIdType: mainVisitor.id_type || "national_id",
      visitorIdNumber: mainVisitor.id_number || "N/A",
      visitorCompany: mainVisitor.company,
      visitorPhone: (mainVisitor.mobile || mainVisitor.phone) || undefined,
      visitorEmail: mainVisitor.email || undefined,
      hostId: Number.isNaN(resolvedHostId) ? undefined : resolvedHostId,
      siteId: parseInt(submissionData.site || submissionData.site_id || submissionData.loc_site) || 1,
      purpose: submissionData.purpose || submissionData.visit_purpose || "Request",
      startDate: submissionData.start_date || new Date().toISOString().split("T")[0],
      endDate: submissionData.end_date || new Date().toISOString().split("T")[0],
      startTime: submissionData.start_time,
      endTime: submissionData.end_time,
      visitors: visitors.map((v: any) => ({
        fullName: v.full_name || v.name,
        idType: v.id_type || "national_id",
        idNumber: v.id_number,
        nationality: v.nationality,
        company: v.company,
        jobTitle: v.job_title,
        phone: (v.mobile || v.phone) || undefined,
        email: v.email || undefined,
        isVerified: v.verified || false,
      })),
      materials: [
        ...(submissionData.material_entry || []).map((m: any) => ({
          materialType: getMaterialTypeName(m.material_type),
          description: m.model || m.description,
          quantity: parseInt(m.quantity) || 1,
          serialNumber: m.serial_number,
          unit: m.unit,
          direction: 'entry',
        })),
        ...(submissionData.material_exit || []).map((m: any) => ({
          materialType: getMaterialTypeName(m.exit_material_type),
          description: m.exit_model || m.exit_description,
          quantity: parseInt(m.exit_quantity) || 1,
          serialNumber: m.exit_serial_number,
          unit: m.exit_unit,
          direction: 'exit',
        })),
      ],
      vehicles: (submissionData.vehicles || []).map((v: any) => ({
        vehicleType: v.vehicle_type,
        plateNumber: v.plate_number,
        driverName: v.driver_name,
        driverIdNumber: v.driver_id_number,
        purpose: v.purpose,
      })),
      submitImmediately: !asDraft,
    };

    if (isEditMode) {
      updateRequest.mutate({ id: editId!, ...requestData } as any);
    } else {
      createRequest.mutate(requestData as any);
    }
  };

  // Check if form is ready (category and types selected)
  const isFormReady = selectedCategoryId && selectedTypeIds.length > 0;
  const isPending = createRequest.isPending || updateRequest.isPending;

  // Show loading state for edit mode
  if (isEditMode && loadingExisting) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5B2C93]" />
      </div>
    );
  }

  return (
    <>
      <ErrorDialogComponent />

      {/* Category/Type Selection Dialog */}
      <CategoryTypeDialog
        open={showCategoryDialog}
        onOpenChange={setShowCategoryDialog}
        categories={(categories as any[]) || []}
        loadingCategories={loadingCategories}
        onConfirm={handleCategoryTypeConfirm}
        initialCategoryId={selectedCategoryId}
        initialTypeIds={selectedTypeIds}
      />

      <div className="flex flex-col h-[calc(100dvh-4rem)] md:h-[calc(100vh-6rem)]">
        {/* Header Toolbar */}
        <div className="bg-white border-b px-3 md:px-6 py-3 md:py-4 flex items-center gap-2 md:gap-4 shadow-sm flex-wrap">
          <Link href="/requests">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-base md:text-xl font-medium text-[#2C2C2C] leading-6 md:leading-7">
              {isEditMode 
                ? t("requests.editRequest", "Edit Request") 
                : t("requests.createNew", "Create New Request")}
            </h1>
            {isEditMode && existingRequest && (
              <p className="text-sm text-[#6B6B6B]">{existingRequest.requestNumber}</p>
            )}
          </div>
          <div className="h-6 w-px bg-[#E0E0E0] ml-1 md:ml-2 hidden md:block" />

          <div className="ml-auto flex items-center gap-2 md:gap-6 text-[#6B6B6B] flex-wrap justify-end">
            {selectedCategory && (
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase font-medium text-[#9CA3AF]">
                  {t("requests.category", "Category")}
                </span>
                <Badge
                  variant="outline"
                  className="bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93] rounded-sm px-2 py-0.5"
                >
                  {isRTL && selectedCategory.nameAr
                    ? selectedCategory.nameAr
                    : selectedCategory.name}
                </Badge>
              </div>
            )}
            {selectedTypes.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase font-medium text-[#9CA3AF]">
                  {t("requests.typesLabel", "Types")}
                </span>
                <div className="flex gap-1">
                  {selectedTypes.map((type: any) => (
                    <Badge
                      key={type.id}
                      className="bg-[#5B2C93] text-white rounded-sm px-2 py-0.5"
                    >
                      {type.shortCode || type.code.toUpperCase()}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {isFormReady && !isEditMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleChangeSelection}
                className="text-[#5B2C93] hover:bg-[#5B2C93]/10 gap-1 h-7 text-xs"
              >
                <Edit2 className="h-3 w-3" />
                {t("common.change", "Change")}
              </Button>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase font-medium text-[#9CA3AF]">
                {t("common.status", "Status")}
              </span>
              <Badge
                variant="outline"
                className={cn(
                  "rounded-sm px-2 py-0.5",
                  isEditMode 
                    ? "bg-[#FEF3C7] text-[#D97706] border-[#D97706]" 
                    : "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]"
                )}
              >
                {isEditMode ? t("common.draft", "DRAFT") : t("common.new", "NEW")}
              </Badge>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden">
          {!isFormReady ? (
            <div className="h-full flex items-center justify-center text-[#6B6B6B]">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-[#9CA3AF]" />
                <p className="text-lg font-medium mb-2">
                  {t("requests.selectCategoryFirst", "Select a category and type to begin")}
                </p>
                <Button
                  onClick={() => setShowCategoryDialog(true)}
                  className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-2"
                >
                  {t("requests.selectCategory", "Select Request Category")}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : loadingForm ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#5B2C93]" />
            </div>
          ) : formDefinition?.sections && formDefinition.sections.length > 0 ? (
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-hidden">
                <DynamicForm
                  sections={formDefinition.sections as FormSection[]}
                  formData={formData}
                  onFormDataChange={setFormData}
                  activeSection={activeSection}
                  onActiveSectionChange={setActiveSection}
                  errors={errors}
                  maxDurationDays={
                    selectedTypes.length > 0
                      ? Math.max(...selectedTypes.map((t: any) => t.maxDurationDays || 180))
                      : undefined
                  }
                />
              </div>
              {/* Bottom action bar */}
              <div className="bg-white border-t px-3 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/requests")}
                  className="text-[#6B6B6B]"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {t("common.cancel", "Cancel")}
                </Button>
                <div className="flex gap-2 md:gap-3 flex-wrap justify-end">
                  <Button
                    variant="outline"
                    onClick={() => handleSubmit(true)}
                    disabled={isPending}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isEditMode 
                      ? t("common.saveDraft", "Save Draft") 
                      : t("common.saveAsDraft", "Save as Draft")}
                  </Button>
                  <Button
                    onClick={() => handleSubmit(false)}
                    disabled={isPending}
                    className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-2"
                  >
                    {isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {t("common.submit", "Submit Request")}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-[#6B6B6B]">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-[#9CA3AF]" />
                <p className="text-lg font-medium mb-2">
                  {t("requests.noFormDefinition", "No form definition found for this request type")}
                </p>
                <p className="text-sm text-[#9CA3AF] mb-4">
                  {t("requests.contactAdmin", "Please contact an administrator to configure the form")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
